import { Effect } from 'effect';

import type { DocKey, StoragePort } from '../document-storage.js';
import {
  DocumentStorageError,
  make as makeDocumentStorage,
  type DocumentStorage,
  type DocumentStorageMethod,
} from '../document-storage-effect.js';
import {
  effectDocumentPersistenceFromPromise,
  type DocumentPersistence,
  type DocumentPersistenceError,
} from './types.js';

function storageFailure(
  method: DocumentStorageMethod,
  key: DocKey,
  error: DocumentPersistenceError,
  mediaName?: string,
): DocumentStorageError {
  return new DocumentStorageError({
    method,
    key,
    mediaName,
    message: error.message,
    cause: error.cause,
  });
}

function mapPersistenceError<A>(
  method: DocumentStorageMethod,
  key: DocKey,
  effect: Effect.Effect<A, DocumentPersistenceError>,
  mediaName?: string,
): Effect.Effect<A, DocumentStorageError> {
  return effect.pipe(Effect.mapError((error) => storageFailure(method, key, error, mediaName)));
}

/** Native document storage over the Node persistence blob table. */
export function persistenceDocumentStorage(store: DocumentPersistence): DocumentStorage {
  const persistence = effectDocumentPersistenceFromPromise(store);
  return makeDocumentStorage({
    putSource: (key, bytes) =>
      mapPersistenceError(
        'putSource',
        key,
        persistence.putBlobEffect({
          documentId: key.documentId,
          kind: 'source',
          name: 'source.pdf',
          contentType: 'application/pdf',
          bytes,
        }),
      ),
    getSource: (key) =>
      mapPersistenceError(
        'getSource',
        key,
        persistence
          .getBlobEffect(key.documentId, 'source', 'source.pdf')
          .pipe(Effect.map((blob) => (blob ? blob.bytes : null))),
      ),
    putMedia: (key, name, bytes, contentType) =>
      mapPersistenceError(
        'putMedia',
        key,
        persistence.putBlobEffect({
          documentId: key.documentId,
          kind: 'media',
          name,
          bytes,
          contentType,
        }),
        name,
      ),
    getMedia: (key, name) =>
      mapPersistenceError(
        'getMedia',
        key,
        persistence
          .getBlobEffect(key.documentId, 'media', name)
          .pipe(
            Effect.map((blob) =>
              blob ? { bytes: blob.bytes, contentType: blob.contentType } : null,
            ),
          ),
        name,
      ),
  });
}

function runStorage<A>(effect: Effect.Effect<A, DocumentStorageError>): Promise<A> {
  return Effect.runPromise(effect.pipe(Effect.catch((error) => Effect.fail(error.cause))));
}

/**
 * Promise StoragePort compatibility adapter. The native DocumentStorage above
 * remains authoritative and restores established persistence causes here.
 */
export function persistenceStorage(store: DocumentPersistence): StoragePort {
  const storage = persistenceDocumentStorage(store);
  return {
    putSource: (key, bytes) => runStorage(storage.putSource(key, bytes)),
    getSource: (key) => runStorage(storage.getSource(key)),
    putMedia: (key, name, bytes, contentType) =>
      runStorage(storage.putMedia(key, name, bytes, contentType)),
    getMedia: (key, name) => runStorage(storage.getMedia(key, name)),
    getSignedUploadUrl: (key, contentType, expiresInSeconds) =>
      runStorage(storage.getSignedUploadUrl(key, contentType, expiresInSeconds)),
  };
}
